import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { REPORT_KEYS } from "../queryKeys";
import { executePinAction, triggerDashboardRefresh, applyGroupFilters } from "../../../services/reportService";
import { PIN_MESSAGES } from "../../../utils/reportsMessages";
import { ApplyFiltersRequest } from "../../../models/pageModels/GroupFilters";

const getMutationErrorMessage = (error: unknown, fallback: string) => {
  const maybeAxiosError = error as { response?: { data?: { error?: string; message?: string } } };
  const message = maybeAxiosError.response?.data?.error || maybeAxiosError.response?.data?.message;
  return message?.replace(/^BadRequest:\s*/, "") || fallback;
};

export function useDashboardMutations(groupId: string) {
  const queryClient = useQueryClient();

  const pinMutation = useMutation({
    mutationFn: ({ action, ...payload }: { action: string; [key: string]: any }) =>
      executePinAction(groupId, action, payload),
    onSuccess: (_, { action }) => {
      toast.success(PIN_MESSAGES[action] || "Dashboard updated");
      queryClient.invalidateQueries({ queryKey: REPORT_KEYS.dashboard(groupId) });
    },
    onError: () => toast.error("Operation failed"),
  });

  const refreshMutation = useMutation({
    mutationFn: () => triggerDashboardRefresh(groupId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: REPORT_KEYS.activeRefreshes });
      await queryClient.cancelQueries({ queryKey: REPORT_KEYS.dashboard(groupId) });

      // The active-refreshes response no longer contains refreshingReports,
      // so we only need to optimistically add the group to refreshingGroups.
      queryClient.setQueryData(REPORT_KEYS.activeRefreshes, (oldData: any) => {
        if (!oldData) return { refreshingGroups: [groupId] };
        return {
          ...oldData,
          refreshingGroups: Array.from(new Set([...oldData.refreshingGroups, groupId]))
        };
      });

      queryClient.setQueryData(REPORT_KEYS.dashboard(groupId), (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          refreshStatus: "In Progress"
        };
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPORT_KEYS.dashboard(groupId) });
    },
    onError: (error) => {
      toast.error(getMutationErrorMessage(error, "Failed to trigger refresh"));
      queryClient.invalidateQueries({ queryKey: REPORT_KEYS.activeRefreshes });
      queryClient.invalidateQueries({ queryKey: REPORT_KEYS.dashboard(groupId) });
    },
  });

  const applyFiltersMutation = useMutation({
    mutationFn: (request: ApplyFiltersRequest) => applyGroupFilters(groupId, request),
    onSuccess: () => {
      toast.success("Filters applied - refreshing dashboard...");

      queryClient.setQueryData(REPORT_KEYS.activeRefreshes, (oldData: any) => {
        if (!oldData) return { refreshingGroups: [groupId] };
        return {
          ...oldData,
          refreshingGroups: Array.from(new Set([...oldData.refreshingGroups, groupId]))
        };
      });

      queryClient.setQueryData(REPORT_KEYS.dashboard(groupId), (oldData: any) => {
        if (!oldData) return oldData;
        return { ...oldData, refreshStatus: "In Progress" };
      });

      queryClient.invalidateQueries({ queryKey: REPORT_KEYS.activeRefreshes });
      queryClient.invalidateQueries({ queryKey: REPORT_KEYS.dashboard(groupId) });
      queryClient.invalidateQueries({ queryKey: REPORT_KEYS.groupFilters(groupId) });
    },
    onError: (error) => toast.error(getMutationErrorMessage(error, "Failed to apply filters")),
  });

  return { pinMutation, refreshMutation, applyFiltersMutation };
}
